import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { UserCircleIcon, LockClosedIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const AccountSettings = () => {
    const { user, updateUser, logout } = useAuth();
    const navigate = useNavigate();

    // Profile state
    const [profileData, setProfileData] = useState({
        name: '',
        email: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);

    // Password state
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Password visibility state
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Email change state
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [emailChangeState, setEmailChangeState] = useState({
        otpSent: false,
        otpVerified: false,
        otp: '',
        newEmail: ''
    });
    const [emailChangeLoading, setEmailChangeLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || ''
            });
        }
    }, [user]);

    const handleProfileChange = (e) => {
        setProfileData({
            ...profileData,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!profileData.name.trim()) {
            toast.error('Name is required');
            return;
        }

        setProfileLoading(true);

        try {
            const response = await axios.put('/api/auth/profile', profileData);

            if (response.data.success) {
                toast.success('Profile updated successfully');
                // Update user context with new data
                if (updateUser) {
                    updateUser(response.data.data);
                }
            } else {
                toast.error(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Profile update error:', error);
            toast.error(error.response?.data?.message || 'An error occurred while updating profile');
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!passwordData.currentPassword) {
            toast.error('Current password is required');
            return;
        }

        if (!passwordData.newPassword) {
            toast.error('New password is required');
            return;
        }

        if (!passwordData.confirmPassword) {
            toast.error('Please confirm your new password');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('New password and confirm password do not match');
            return;
        }

        setPasswordLoading(true);

        try {
            const response = await axios.put('/api/auth/change-password', passwordData);

            if (response.data.success) {
                toast.success('Password changed successfully. Please log in again.');
                logout(); // Log the user out
                // Clear password fields (though we are logging out so this component will unmount)
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            } else {
                toast.error(response.data.message || 'Failed to change password');
            }
        } catch (error) {
            console.error('Password change error:', error);
            toast.error(error.response?.data?.message || 'An error occurred while changing password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleSendOTP = async () => {
        setEmailChangeLoading(true);
        try {
            const response = await axios.post('/api/auth/request-email-change');
            if (response.data.success) {
                toast.success('OTP sent to your current registered email.');
                setEmailChangeState(prev => ({ ...prev, otpSent: true, otpVerified: false, otp: '' }));
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error sending OTP');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!emailChangeState.otp || emailChangeState.otp.length < 6) {
            toast.error('Please enter the 6-digit verification code');
            return;
        }

        setEmailChangeLoading(true);
        try {
            const response = await axios.post('/api/auth/verify-email-otp', {
                otp: emailChangeState.otp
            });
            if (response.data.success) {
                toast.success('OTP verified successfully');
                setEmailChangeState(prev => ({ ...prev, otpVerified: true }));
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error verifying OTP');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleUpdateEmail = async (e) => {
        e.preventDefault();
        
        if (!emailChangeState.newEmail) {
            toast.error('Please provide a new email address');
            return;
        }

        setEmailChangeLoading(true);
        try {
            const response = await axios.put('/api/auth/update-email', {
                otp: emailChangeState.otp,
                newEmail: emailChangeState.newEmail
            });
            if (response.data.success) {
                toast.success('Email updated successfully. Please login again with your new email address.');
                
                setIsEmailModalOpen(false);
                setEmailChangeState({ otpSent: false, otpVerified: false, otp: '', newEmail: '' });
                
                // Log out user securely
                if (logout) {
                    logout();
                } else {
                    localStorage.removeItem('token');
                    if (updateUser) updateUser(null);
                }
                
                // Navigate to login
                setTimeout(() => {
                    navigate('/login');
                }, 1000);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating email');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const [activeTab, setActiveTab] = useState('profile');

    return (
        <div className="min-h-[calc(100vh-64px)] bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Account Settings</h1>
                    <p className="mt-1.5 text-sm text-gray-500">
                        Manage your profile information and account security
                    </p>
                </div>

                <div className="flex flex-col md:flex-row gap-8">
                    {/* Left Column - Navigation */}
                    <div className="w-full md:w-56 shrink-0">
                        <nav className="flex flex-col gap-1.5">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === 'profile'
                                        ? 'bg-white text-indigo-700 shadow-sm border border-gray-200/60'
                                        : 'text-gray-600 hover:bg-gray-100 border border-transparent hover:text-gray-900'
                                    }`}
                            >
                                <UserCircleIcon className="h-5 w-5" />
                                Profile
                            </button>
                            <button
                                onClick={() => setActiveTab('security')}
                                className={`flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === 'security'
                                        ? 'bg-white text-indigo-700 shadow-sm border border-gray-200/60'
                                        : 'text-gray-600 hover:bg-gray-100 border border-transparent hover:text-gray-900'
                                    }`}
                            >
                                <LockClosedIcon className="h-5 w-5" />
                                Security
                            </button>
                        </nav>
                    </div>

                    {/* Right Column - Content Panel */}
                    <div className="flex-1">
                        {activeTab === 'profile' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                <div className="px-6 py-5 border-b border-gray-100">
                                    <h2 className="text-lg font-semibold text-gray-900">Profile Details</h2>
                                    <p className="mt-1 text-xs text-gray-500">Update your public profile information.</p>
                                </div>

                                <form onSubmit={handleProfileSubmit} className="p-6">
                                    <div className="max-w-md space-y-5">
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={profileData.name}
                                                onChange={handleProfileChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-all"
                                                placeholder="Enter full name"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Email Address
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={profileData.email}
                                                    readOnly
                                                    disabled
                                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEmailModalOpen(true)}
                                                    className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-indigo-600 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    Change Email
                                                </button>
                                            </div>
                                            <p className="mt-1.5 text-[11px] text-gray-400">Click the button to securely update your email address.</p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Account Role
                                            </label>
                                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                                                <span className="capitalize text-sm text-gray-700 font-medium">{user?.role}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-5 border-t border-gray-100 flex items-center justify-end">
                                        <button
                                            type="submit"
                                            disabled={profileLoading}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                        >
                                            {profileLoading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {activeTab === 'security' && (
                            <div className="space-y-6">
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                                    <div className="px-6 py-5 border-b border-gray-100">
                                        <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
                                        <p className="mt-1 text-xs text-gray-500">Ensure your account is using a long, random password to stay secure.</p>
                                    </div>

                                    <form onSubmit={handlePasswordSubmit} className="p-6">
                                        <div className="max-w-md space-y-5">
                                            <div>
                                                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Current Password <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showCurrentPassword ? "text" : "password"}
                                                        id="currentPassword"
                                                        name="currentPassword"
                                                        value={passwordData.currentPassword}
                                                        onChange={handlePasswordChange}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10"
                                                        placeholder="••••••••"
                                                        required
                                                    />
                                                    {passwordData.currentPassword && (
                                                        <button
                                                            type="button"
                                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                        >
                                                            {showCurrentPassword ? (
                                                                <EyeSlashIcon className="h-4 w-4" />
                                                            ) : (
                                                                <EyeIcon className="h-4 w-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    New Password <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showNewPassword ? "text" : "password"}
                                                        id="newPassword"
                                                        name="newPassword"
                                                        value={passwordData.newPassword}
                                                        onChange={handlePasswordChange}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10"
                                                        placeholder="••••••••"
                                                        required
                                                    />
                                                    {passwordData.newPassword && (
                                                        <button
                                                            type="button"
                                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                                        >
                                                            {showNewPassword ? (
                                                                <EyeSlashIcon className="h-4 w-4" />
                                                            ) : (
                                                                <EyeIcon className="h-4 w-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="mt-1.5 text-[11px] text-gray-500">
                                                    Must be at least 6 characters long.
                                                </p>
                                            </div>

                                            <div>
                                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                                                    Confirm New Password <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showConfirmPassword ? "text" : "password"}
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        value={passwordData.confirmPassword}
                                                        onChange={handlePasswordChange}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10"
                                                        placeholder="••••••••"
                                                        required
                                                    />
                                                    {passwordData.confirmPassword && (
                                                        <button
                                                            type="button"
                                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        >
                                                            {showConfirmPassword ? (
                                                                <EyeSlashIcon className="h-4 w-4" />
                                                            ) : (
                                                                <EyeIcon className="h-4 w-4" />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-8 pt-5 border-t border-gray-100 flex items-center justify-end">
                                            <button
                                                type="submit"
                                                disabled={passwordLoading}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                            >
                                                {passwordLoading ? (
                                                    <>
                                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    'Update Password'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="bg-indigo-50 border border-indigo-100/50 rounded-xl p-5">
                                    <h3 className="text-sm font-semibold text-indigo-900 mb-2">Security Tips</h3>
                                    <ul className="space-y-1.5 text-[13px] text-indigo-800">
                                        <li className="flex items-start gap-2">
                                            <CheckCircleIcon className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                                            <span>Use a long, unique password consisting of mixed characters.</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircleIcon className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                                            <span>We will automatically log you out upon a successful password change.</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Email Change Modal */}
            {isEmailModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Change Email Address
                            </h3>
                            <button
                                onClick={() => {
                                    setIsEmailModalOpen(false);
                                    setEmailChangeState({ otpSent: false, otpVerified: false, otp: '', newEmail: '' });
                                }}
                                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="p-6">
                            <form onSubmit={handleUpdateEmail}>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Old Email Address (Current)
                                        </label>
                                        <input
                                            type="email"
                                            value={profileData.email}
                                            disabled
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 sm:text-sm cursor-not-allowed mb-2"
                                        />
                                        {!emailChangeState.otpVerified && (
                                            <button
                                                type="button"
                                                onClick={handleSendOTP}
                                                disabled={emailChangeLoading}
                                                className="w-full justify-center inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
                                            >
                                                {emailChangeState.otpSent ? 'Resend OTP' : 'Send Verification OTP'}
                                            </button>
                                        )}
                                    </div>

                                    {emailChangeState.otpSent && !emailChangeState.otpVerified && (
                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-center">
                                                Enter the 6-digit OTP sent to your old email
                                            </label>
                                            <input
                                                type="text"
                                                value={emailChangeState.otp}
                                                onChange={(e) => setEmailChangeState({...emailChangeState, otp: e.target.value.replace(/[^0-9]/g, '').slice(0, 6)})}
                                                disabled={emailChangeState.otpVerified}
                                                className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-center text-xl font-mono tracking-widest uppercase transition-all mb-3"
                                                placeholder="------"
                                                maxLength="6"
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                onClick={handleVerifyOTP}
                                                disabled={emailChangeLoading || emailChangeState.otp.length < 6}
                                                className="w-full justify-center inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50"
                                            >
                                                {emailChangeLoading ? 'Verifying...' : 'Verify OTP'}
                                            </button>
                                        </div>
                                    )}

                                    <div>
                                        <label className={`block text-sm font-medium mb-1.5 ${emailChangeState.otpVerified ? 'text-gray-700' : 'text-gray-400'}`}>
                                            New Email Address {emailChangeState.otpVerified && <span className="text-green-600 ml-1 text-xs font-semibold">✓ Verified to edit</span>}
                                        </label>
                                        <input
                                            type="email"
                                            value={emailChangeState.newEmail}
                                            onChange={(e) => setEmailChangeState({...emailChangeState, newEmail: e.target.value})}
                                            disabled={!emailChangeState.otpVerified}
                                            className={`w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm transition-all ${emailChangeState.otpVerified ? 'border-indigo-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white' : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'}`}
                                            placeholder={emailChangeState.otpVerified ? "Enter your new email address" : "Verify OTP first"}
                                            required={emailChangeState.otpVerified}
                                        />
                                    </div>
                                </div>
                                
                                <div className="mt-8 pt-5 border-t border-gray-100 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEmailModalOpen(false);
                                            setEmailChangeState({ otpSent: false, otpVerified: false, otp: '', newEmail: '' });
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={emailChangeLoading || !emailChangeState.otpVerified || !emailChangeState.newEmail}
                                        className="inline-flex items-center px-5 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors shadow-sm"
                                    >
                                        {emailChangeLoading ? 'Processing...' : 'Save New Email'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSettings;
